import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useGetTrendingTagsQuery } from '../../store/api/discussionsApi'
import { useGetSimilarUsersQuery, useFollowUserMutation, useGetPreferencesQuery, useUpdatePreferencesMutation } from '../../store/api/usersApi'
import { useAppSelector } from '../../hooks/useAppDispatch'
import Avatar from '../ui/Avatar'

export default function LeftSidebar() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const activeTag = searchParams.get('tag') ?? ''
  const { data: tagsData, isLoading: trendingTagsLoading } = useGetTrendingTagsQuery()
  const trendingTags = tagsData?.tags ?? []
  const { user, isAuthenticated } = useAppSelector((s) => s.auth)
  const { data: similarData } = useGetSimilarUsersQuery(user?.id ?? '', {
    skip: !isAuthenticated || !user?.id,
  })
  const { data: prefsData } = useGetPreferencesQuery(undefined, { skip: !isAuthenticated })
  const [updatePreferences] = useUpdatePreferencesMutation()
  const [followUser] = useFollowUserMutation()
  const similarUsers = similarData?.users ?? []
  const followedTags: string[] = (prefsData?.preferences as any)?.followedTags ?? []

  const handleToggleTagFollow = async (slug: string) => {
    const updated = followedTags.includes(slug)
      ? followedTags.filter(t => t !== slug)
      : [...followedTags, slug]
    await updatePreferences({ followedTags: updated } as any).catch(() => {})
  }

  return (
    <aside style={{
      width: '220px', minWidth: '220px',
      paddingTop: '1rem', paddingBottom: '1rem', paddingRight: '0.75rem',
      display: 'flex', flexDirection: 'column', gap: '0.75rem',
      height: '100%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
    }}>

      {/* New Discussion */}
      <button
        onClick={() => void navigate('/new')}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
          width: '100%', padding: '0.65rem', borderRadius: '10px',
          background: 'var(--accent-primary)', color: 'white',
          border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
          flexShrink: 0,
        }}
      >
        ➕ New Discussion
      </button>

      {/* People Like You — shown first, more valuable than tags */}
      {isAuthenticated && similarUsers.length > 0 && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '12px', padding: '0.875rem', flexShrink: 0,
        }}>
          <div style={{
            color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: '0.75rem',
            display: 'flex', alignItems: 'center', gap: '0.35rem',
          }}>
            <span>🤝</span> People Like You
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', maxHeight: '200px', overflowY: 'auto' }}>
            {similarUsers.map(u => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Link
                  to={`/profile/${u.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0, textDecoration: 'none' }}
                >
                  <Avatar name={u.displayName} avatarUrl={u.avatarUrl} size={30} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {u.displayName}
                    </div>
                    {u.sharedCategories.length > 0 && (
                      <div style={{
                        fontSize: '0.67rem', color: 'var(--text-muted)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        marginTop: '0.1rem',
                      }}>
                        {u.sharedCategories.map(c => `${c.icon ?? ''} ${c.name}`).join(' · ')}
                      </div>
                    )}
                  </div>
                </Link>
                <button
                  onClick={() => void followUser(u.id)}
                  title={`Follow ${u.displayName}`}
                  style={{
                    flexShrink: 0, width: '26px', height: '26px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1rem', lineHeight: 1,
                    background: 'rgba(124,58,237,0.1)',
                    color: 'var(--accent-primary)',
                    border: '1px solid rgba(124,58,237,0.25)',
                    borderRadius: '50%', cursor: 'pointer',
                    fontWeight: 700, transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    const b = e.currentTarget as HTMLButtonElement
                    b.style.background = 'var(--accent-primary)'
                    b.style.color = 'white'
                  }}
                  onMouseLeave={(e) => {
                    const b = e.currentTarget as HTMLButtonElement
                    b.style.background = 'rgba(124,58,237,0.1)'
                    b.style.color = 'var(--accent-primary)'
                  }}
                >
                  +
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trending Tags — fills remaining height and scrolls its own list,
          same pattern as RightSidebar's Recent Activity card, instead of
          growing the whole sidebar column. */}
      {!trendingTagsLoading && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '12px', padding: '0.875rem',
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: '0.6rem', paddingLeft: '0.25rem',
            display: 'flex', alignItems: 'center', gap: '0.35rem',
            flexShrink: 0,
          }}>
            <span>🔥</span> Trending Tags
          </div>
          {trendingTags.length === 0 ? (
            <div style={{ padding: '0.3rem 0.25rem', fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              No trending tags yet — check back soon.
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {trendingTags.map((tag) => {
              const isFollowed = followedTags.includes(tag.slug)
              return (
                <div key={tag.id} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Link
                    to={activeTag === tag.slug ? '/' : `/search?tag=${tag.slug}`}
                    style={{
                      textDecoration: 'none', flex: 1, display: 'inline-flex', alignItems: 'center',
                      padding: '0.3rem 0.6rem', borderRadius: '20px', fontSize: '0.78rem',
                      fontWeight: activeTag === tag.slug ? 600 : 400,
                      background: activeTag === tag.slug ? 'var(--accent-primary)' : 'var(--bg-elevated)',
                      color: activeTag === tag.slug ? 'white' : 'var(--text-secondary)',
                      border: `1px solid ${activeTag === tag.slug ? 'var(--accent-primary)' : 'var(--border)'}`,
                      transition: 'all 0.15s', minWidth: 0, overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      if (activeTag !== tag.slug) {
                        const el = e.currentTarget as HTMLAnchorElement
                        el.style.borderColor = 'var(--accent-primary)'
                        el.style.color = 'var(--accent-primary)'
                        el.style.background = 'var(--accent-light)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeTag !== tag.slug) {
                        const el = e.currentTarget as HTMLAnchorElement
                        el.style.borderColor = 'var(--border)'
                        el.style.color = 'var(--text-secondary)'
                        el.style.background = 'var(--bg-elevated)'
                      }
                    }}
                  >
                    <span style={{ opacity: 0.6, marginRight: '0.1rem', flexShrink: 0 }}>#</span>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tag.name}</span>
                  </Link>
                  {/* Follow/unfollow button — only for authenticated users */}
                  {isAuthenticated && (
                    <button
                      onClick={() => void handleToggleTagFollow(tag.slug)}
                      title={isFollowed ? `Unfollow #${tag.name}` : `Follow #${tag.name}`}
                      style={{
                        flexShrink: 0, width: '22px', height: '22px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', lineHeight: 1,
                        background: isFollowed ? 'var(--accent-primary)' : 'rgba(124,58,237,0.1)',
                        color: isFollowed ? 'white' : 'var(--accent-primary)',
                        border: `1px solid ${isFollowed ? 'var(--accent-primary)' : 'rgba(124,58,237,0.25)'}`,
                        borderRadius: '50%', cursor: 'pointer',
                        fontWeight: 700, transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        const b = e.currentTarget as HTMLButtonElement
                        if (!isFollowed) {
                          b.style.background = 'var(--accent-primary)'
                          b.style.color = 'white'
                        } else {
                          b.style.background = 'rgba(239,68,68,0.1)'
                          b.style.color = 'var(--error)'
                          b.style.borderColor = 'rgba(239,68,68,0.4)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        const b = e.currentTarget as HTMLButtonElement
                        b.style.background = isFollowed ? 'var(--accent-primary)' : 'rgba(124,58,237,0.1)'
                        b.style.color = isFollowed ? 'white' : 'var(--accent-primary)'
                        b.style.borderColor = isFollowed ? 'var(--accent-primary)' : 'rgba(124,58,237,0.25)'
                      }}
                    >
                      {isFollowed ? '✓' : '+'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
          )}
        </div>
      )}
    </aside>
  )
}
